import React, { useRef, useEffect } from 'react';
import { gsap } from '../../lib/gsapSetup';
import { useTranslation } from '../../i18n/LanguageContext';
import clientApp from '../../assets/client-app.webp';
import driverApp from '../../assets/driver-app.webp';

const APP_DATA = [
  {
    id: 'client',
    eyebrowKey: 'panels.client.title',
    titleKey: 'panels.client.title',
    descKey: 'panels.client.desc',
    image: clientApp,
    alt: 'Application client LinxExpress',
    pointKeys: [
      'panels.client.f1',
      'panels.client.f2',
      'panels.client.f3',
      'panels.client.f4',
    ],
  },
  {
    id: 'driver',
    eyebrowKey: 'panels.driver.title',
    titleKey: 'panels.driver.title',
    descKey: 'panels.driver.desc',
    image: driverApp,
    alt: 'Application transporteur LinxExpress',
    pointKeys: [
      'panels.driver.f1',
      'panels.driver.f2',
      'panels.driver.f3',
      'panels.driver.f4',
    ],
  },
];

export default function PinnedPanels() {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const phoneRefs = useRef([]);
  const textRefs = useRef([]);
  const { t } = useTranslation();

  useEffect(() => {
    if (!sectionRef.current) return;

    const ctx = gsap.context(() => {
      // Heading reveal
      if (headingRef.current) {
        gsap.fromTo(
          headingRef.current,
          { opacity: 0, y: 36 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power3.out',
            scrollTrigger: { trigger: headingRef.current, start: 'top 82%' },
          },
        );
      }

      // Phone mockups: slide up + fade, staggered
      phoneRefs.current.forEach((el, i) => {
        if (!el) return;
        gsap.fromTo(
          el,
          { opacity: 0, y: 70 },
          {
            opacity: 1,
            y: 0,
            duration: 1.1,
            delay: i * 0.15,
            ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 84%' },
          },
        );
      });

      // Text blocks fade in
      textRefs.current.forEach((el, i) => {
        if (!el) return;
        gsap.fromTo(
          el,
          { opacity: 0, y: 24 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            delay: i * 0.12,
            ease: 'power2.out',
            scrollTrigger: { trigger: el, start: 'top 86%' },
          },
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id='app'
      className='relative py-28 lg:py-36 bg-linx-bg overflow-hidden'
    >
      <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-secondary/[0.08] blur-[140px] pointer-events-none' />
      <div className='section-container relative z-10'>
        <div className='text-center mb-16 lg:mb-20'>
          <span className='inline-block font-mono text-xs text-secondary-dark tracking-[0.22em] uppercase mb-4 opacity-80'>
            {t('panels.label')}
          </span>
          <h2
            ref={headingRef}
            className='font-display text-[clamp(2rem,4.5vw,3.75rem)] font-bold text-primary leading-[1.05]'
          >
            {t('panels.intro.title')}
          </h2>
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-10 items-start'>
          {APP_DATA.map((app, i) => (
            <div
              key={app.id}
              className='flex flex-col items-center text-center gap-8'
            >
              <div
                ref={(el) => {
                  phoneRefs.current[i] = el;
                }}
                className='relative w-[min(72vw,260px)]'
              >
                <div className='absolute -inset-4 rounded-[50px] bg-primary/[0.06] blur-2xl' />
                <div className='relative rounded-[2.4rem] border border-primary/[0.1] overflow-hidden shadow-[0_32px_80px_-16px_rgba(10,23,78,0.18)]'>
                  <img
                    src={app.image}
                    alt={app.alt}
                    className='w-full h-auto block'
                    loading='lazy'
                  />
                </div>
                <span className='absolute -top-2 -right-2 w-5 h-5 rounded-full bg-secondary shadow-md shadow-secondary/30' />
              </div>

              <div
                ref={(el) => {
                  textRefs.current[i] = el;
                }}
                className='max-w-xs'
              >
                <span className='font-mono text-[10px] tracking-[0.22em] uppercase text-secondary-dark opacity-70 mb-2 block'>
                  {t(app.eyebrowKey)}
                </span>
                <h3 className='font-display text-2xl sm:text-3xl font-bold text-primary mb-3'>
                  {t(app.titleKey)}
                </h3>
                <p className='text-sm sm:text-base leading-relaxed mb-5 text-primary/60'>
                  {t(app.descKey)}
                </p>
                <div className='flex flex-wrap justify-center gap-2'>
                  {app.pointKeys.map((pk) => (
                    <span
                      key={pk}
                      className='px-3 py-1 rounded-full text-[11px] font-semibold bg-primary/[0.06] text-primary border border-primary/[0.08]'
                    >
                      {t(pk)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className='mt-20 lg:mt-24 h-px w-full max-w-sm mx-auto bg-gradient-to-r from-transparent via-secondary/50 to-transparent' />
      </div>
    </section>
  );
}
